import {
  fetchExperienceCategories,
  fetchExperienceListFacets,
} from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { mapTravelStylesToTargetAudience } from 'lib/experiences/experienceUtils'
import {
  getExperienceSearchFacetKey,
  isExperienceSearchStreamingPath,
  shouldUseExperienceFacetStreaming,
} from 'lib/experiences/experienceSearchStreaming'
import {
  buildPrimaryExperienceCategories,
  type ExperiencePrimaryCategoryData,
} from 'lib/experiences/experiencesHomepageUtils'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import { useEffect, useMemo } from 'react'
import useExperiencesSearchFilters from './useExperiencesSearchFilters'

interface UseMostPopulatedSearchExperienceFacetsOptions {
  rankCategoriesByRelevance?: boolean;
  prioritiseSelectedCategories?: boolean;
  useParentCategoryCounts?: boolean;
}

function useMostPopulatedSearchExperienceFacets(
  place?: App.PlaceIdentity,
  options: UseMostPopulatedSearchExperienceFacetsOptions = {},
): {
  primaryCategories: Array<ExperiencePrimaryCategoryData>;
  categories: Array<App.ExperienceCategoryFacet>;
  collections: Array<App.ExperienceCollectionFacet>;
  areas: Array<App.ExperienceAreaFacet>;
  priceRange: App.ExperienceFacets['price'];
  leExclusiveCount: number;
  freeCancellationCount: number;
  instantConfirmationCount: number;
  giftingAvailableCount: number;
  luxPlusMemberPriceCount: number;
  ratingCounts: Array<App.ExperienceRatingCountFacet>;
  targetAudienceCounts: Array<App.ExperienceTargetAudienceCountFacet>;
  timeSlotCounts: Array<App.ExperienceTimeSlotCountFacet>;
  fetching: boolean;
} {
  const dispatch = useAppDispatch()
  const categories = useAppSelector(
    (state) => state.experience.experienceCategories,
  )
  const searchFacets = useAppSelector(
    (state) => state.experience.experienceListFacets,
  )
  const categoriesError = useAppSelector(
    (state) => state.experience.experienceCategoriesError,
  )
  const useFacetStreaming = useAppSelector(state =>
    shouldUseExperienceFacetStreaming(state, place?.id),
  )
  const isSearchOrMapPage = useAppSelector(state =>
    isExperienceSearchStreamingPath(state.router.location.pathname),
  )
  const isLuxPlusEnabled = useAppSelector((state) =>
    getIsEnabledLuxPlusDiscountedExperiences(state),
  )
  const { searchFilters } = useExperiencesSearchFilters({ appliedOnly: true })

  const targetAudienceList = useMemo(
    () => searchFilters.travelStyles?.length ? mapTravelStylesToTargetAudience(searchFilters.travelStyles) : undefined,
    [searchFilters.travelStyles],
  )

  const facetFilters = useMemo(() => ({
    availableFrom: searchFilters.checkIn,
    availableTo: searchFilters.checkOut,
    priceGte: searchFilters.priceGte,
    priceLte: searchFilters.priceLte,
    leExclusive: searchFilters.leExclusive,
    luxPlusExclusive: searchFilters.luxPlusFeatures?.join(','),
    ratingRange: searchFilters.minRating !== undefined ? String(searchFilters.minRating) : undefined,
    targetAudience: targetAudienceList?.join(','),
    timeSlots: searchFilters.timeSlots?.join(',') || undefined,
  }), [searchFilters, targetAudienceList])

  const facetKey = useMemo(
    () => place ? getExperienceSearchFacetKey(place.id, {
      from: searchFilters.checkIn,
      to: searchFilters.checkOut,
      priceGte: searchFilters.priceGte,
      priceLte: searchFilters.priceLte,
      leExclusive: searchFilters.leExclusive,
      luxPlusFeatures: searchFilters.luxPlusFeatures,
      minRating: searchFilters.minRating,
      targetAudience: targetAudienceList,
      timeSlots: searchFilters.timeSlots,
    }) : '',
    [place, searchFilters, targetAudienceList],
  )

  useEffect(() => {
    if (!place) {
      return
    }

    dispatch(fetchExperienceCategories())
    if (useFacetStreaming) {
      if (isSearchOrMapPage) {
        if (categories.length) return
      } else if (!categories.length && !categoriesError) {
        return
      }
    }

    dispatch(fetchExperienceListFacets(place.id, facetFilters))
  }, [place, facetFilters, dispatch, useFacetStreaming, isSearchOrMapPage, categories.length, categoriesError])

  const activeFacetsState = useMemo(() => {
    return searchFacets[facetKey]
  }, [facetKey, searchFacets])

  const categoriesFacets = useMemo<Array<App.ExperienceCategoryFacet>>(() => {
    if (!place) {
      return []
    }

    return activeFacetsState?.facets?.category ?? []
  }, [place, activeFacetsState])
  const activeLuxPlusMemberPriceCount = activeFacetsState?.facets?.luxPlusMemberPriceCount

  const primaryCategories = useMemo<Array<ExperiencePrimaryCategoryData>>(() => {
    return buildPrimaryExperienceCategories({
      categories,
      categoriesFacets,
      isLuxPlusEnabled,
      rankCategoriesByRelevance: options.rankCategoriesByRelevance,
      prioritiseSelectedCategories: options.prioritiseSelectedCategories,
      selectedCategoryCodes: options.prioritiseSelectedCategories ? searchFilters.categoryCodes : undefined,
      luxPlusMemberPriceCount: activeLuxPlusMemberPriceCount,
      useParentCategoryCounts: options.useParentCategoryCounts,
    })
  }, [
    activeLuxPlusMemberPriceCount,
    categories,
    categoriesFacets,
    isLuxPlusEnabled,
    options.prioritiseSelectedCategories,
    options.rankCategoriesByRelevance,
    options.useParentCategoryCounts,
    searchFilters.categoryCodes,
  ])

  const collections = useMemo<Array<App.ExperienceCollectionFacet>>(() => {
    if (!place) {
      return []
    }

    return activeFacetsState?.facets?.collection ?? []
  }, [place, activeFacetsState])

  const areas = useMemo<Array<App.ExperienceAreaFacet>>(() => {
    return place ? activeFacetsState?.facets?.areas ?? [] : []
  }, [place, activeFacetsState])

  const priceRange = useMemo<App.ExperienceFacets['price']>(() => {
    if (!place) {
      return { min: 0, max: 0 }
    }

    return activeFacetsState?.facets?.price ?? { min: 0, max: 0 }
  }, [activeFacetsState, place])

  // If we don't have a place, we can't fetch facets (API is keyed by place id),
  // so avoid showing an "infinite" loading state.
  const fetching = !!place && (!activeFacetsState || Boolean(activeFacetsState.fetching))

  const leExclusiveCount = useMemo(() => {
    return place ? activeFacetsState?.facets?.leExclusiveCount ?? 0 : 0
  }, [place, activeFacetsState])

  const ratingCounts = useMemo(() => {
    return place ? activeFacetsState?.facets?.ratingCounts ?? [] : []
  }, [place, activeFacetsState])

  const targetAudienceCounts = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.targetAudienceCounts ?? []
      : []
  }, [place, activeFacetsState])

  const freeCancellationCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.freeCancellationCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const instantConfirmationCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.instantConfirmationCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const giftingAvailableCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.giftingAvailableCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const luxPlusMemberPriceCount = useMemo(() => {
    return place
      ? activeFacetsState?.facets?.luxPlusMemberPriceCount ?? 0
      : 0
  }, [place, activeFacetsState])

  const timeSlotCounts = useMemo(() => {
    return place ? activeFacetsState?.facets?.timeSlotCounts ?? [] : []
  }, [place, activeFacetsState])

  return {
    primaryCategories,
    categories: categoriesFacets,
    collections,
    areas,
    priceRange,
    leExclusiveCount,
    freeCancellationCount,
    instantConfirmationCount,
    giftingAvailableCount,
    luxPlusMemberPriceCount,
    ratingCounts,
    targetAudienceCounts,
    timeSlotCounts,
    fetching,
  }
}

export default useMostPopulatedSearchExperienceFacets
